import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setAlgoFormData } from '../store';

const Step2ConfigureAnalysis = ({ onNext, onBack }) => {
    const algoFormData = useSelector((state) => state.analysisData.algoFormData);
    const dispatch = useDispatch();

    const [algorithm, setAlgorithm] = useState('apriori');
    const [support, setSupport] = useState(1);
    const [sizes, setSizes] = useState({ 2: true, 3: true, 4: false });
    // const [algoFormData, setAlgoFormData] = useState({
    //     ALGORITHM: "apriori",
    //     MIN_SUPPORT: 0.01,
    //     ANALYSIS_SCOPE: {
    //         isPairs: true,
    //         isTriples: false,
    //         isQuadruples: false,
    //         isAll: false
    //     },
    //     isExcludeSingleOrder: false
    // })

    const handleChange = (e) => {
        const value = e.target.name == "isExcludeSingleOrder" ? e.target.checked : e.target.value;
        const key = e.target.name;

        dispatch(setAlgoFormData({
            ...algoFormData,
            [key]: value
        }))
    }

    useEffect(() => {
        let percentage = support
        let min_support = percentage / 100
        dispatch(
            setAlgoFormData({
                ...algoFormData,
                MIN_SUPPORT: min_support
            })
        )
    }, [support])

    const handleAnalysisScope = (e) => {
        const { name, checked } = e.target;

        const currentScope = algoFormData.ANALYSIS_SCOPE;

        let updatedScope;

        if (name === "isAll" && checked) {
            updatedScope = {
                isAll: true,
                isPairs: true,
                isTriples: true,
                isQuadruples: true,
            };
        } else {
            updatedScope = {
                ...currentScope,
                [name]: checked,
            };

            // Ensure at least one of the main checkboxes stays checked
            const oneChecked = ["isPairs", "isTriples", "isQuadruples"].some(
                (key) => updatedScope[key]
            );

            if (!oneChecked) {
                updatedScope.isPairs = true; // default to isPairs
            }

            updatedScope.isAll = false; // force isAll to false if not explicitly checked
        }

        dispatch(
            setAlgoFormData({
                ...algoFormData,
                ANALYSIS_SCOPE: updatedScope,
            })
        );
    };
    const handleConfigure = () => {
        onNext(); // simulate save
    };

    return (
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <i className="fas fa-sliders-h mr-3 text-indigo-500"></i> Configure Your Analysis
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
                <div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Algorithm Selection</label>
                        <div className="space-y-3">
                            {['apriori', 'fpgrowth'].map((algo) => (
                                <div key={algo} className="flex items-center">
                                    <input
                                        type="radio"
                                        id={algo}
                                        name="ALGORITHM"
                                        value={algo}
                                        checked={algoFormData.ALGORITHM === algo}
                                        onChange={(e) => handleChange(e)}
                                        className="h-4 w-4 text-indigo-600 border-gray-300"
                                    />
                                    <label htmlFor={algo} className="ml-3 block text-sm font-medium text-gray-700">
                                        {algo === 'apriori' ? 'Apriori Algorithm' : 'FP-Growth Algorithm'}
                                        <span class="text-xs text-gray-500 block">{algo === 'apriori' ? 'Better for smaller datasets with clear patterns' : 'Better for larger datasets with complex patterns'}</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Minimum Support Threshold (%)
                        </label>

                        <div className="relative">
                            <input
                                type="number"
                                // min={0.01}
                                max={100}
                                step={0.01}
                                required
                                value={support}
                                onChange={(e) => setSupport(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-4 py-2 pr-10 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Enter support value"
                            />
                            <span className="absolute inset-y-0 right-3 flex items-center text-gray-500 text-sm pointer-events-none">
                                %
                            </span>
                        </div>

                        <p className="mt-2 text-xs text-gray-500">
                            The minimum frequency of itemsets to be considered <b>{algoFormData.MIN_SUPPORT}</b>.
                        </p>
                    </div>

                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Analysis Scope</label>
                    <div className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            checked={algoFormData.ANALYSIS_SCOPE.isPairs}
                            onChange={handleAnalysisScope}
                            name='isPairs'
                            className="h-4 w-4 text-indigo-600"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                            Include itemsets of size pairs
                        </label>
                    </div>
                    <div className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            checked={algoFormData.ANALYSIS_SCOPE.isTriples}
                            onChange={handleAnalysisScope}
                            name='isTriples'
                            className="h-4 w-4 text-indigo-600"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                            Include itemsets of size triples
                        </label>
                    </div>
                    <div className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            checked={algoFormData.ANALYSIS_SCOPE.isQuadruples}
                            onChange={handleAnalysisScope}
                            name='isQuadruples'
                            className="h-4 w-4 text-indigo-600"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                            Include itemsets of size quadruples
                        </label>
                    </div>
                    <div className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            checked={algoFormData.ANALYSIS_SCOPE.isAll}
                            onChange={handleAnalysisScope}
                            name='isAll'
                            className="h-4 w-4 text-indigo-600"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                            All
                        </label>
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-3"> Single Orders</label>
                    <div className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            checked={algoFormData.isExcludeSingleOrder}
                            onChange={handleChange}
                            name='isExcludeSingleOrder'
                            className="h-4 w-4 text-indigo-600"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                            Exclude Single Orders
                        </label>
                    </div>
                    <br />
                    <div className="flex justify-between mt-10">
                        <button onClick={onBack} className="text-gray-600 hover:text-gray-800 py-2 px-4">
                            <i className="fas fa-arrow-left mr-2" /> Back
                        </button>
                        <button
                            onClick={handleConfigure}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-md flex items-center"
                        >
                            Continue <i className="fas fa-arrow-right ml-2" />
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default Step2ConfigureAnalysis;
